// src/components/layout/Header.tsx - Premium Modern Creative Header

import { useEffect, useState, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Menu,
  X,
  Globe,
  Sparkles,
  Facebook,
  Instagram,
  Youtube,
  Twitter,
  Linkedin,
  Mail,
  Phone,
  MessageCircle,
  ChevronRight,
  Trophy,
  Users,
  Newspaper,
  Shield,
  Flame,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import { Button } from "@/components/ui/button";
import TikTokIcon from "@/components/icons/TikTokIcon";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo.png";

type NavItem = {
  key: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  label: { ar: string; en: string };
  description?: { ar: string; en: string };
};

type SocialLink = {
  key: string;
  href: string;
  label: { ar: string; en: string };
  Icon: React.ComponentType<{ className?: string }>;
  color: string;
};

const Header = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const location = useLocation();
  const headerRef = useRef<HTMLElement>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const toggleLanguage = () => {
    const newLang = i18n.language === "ar" ? "en" : "ar";
    i18n.changeLanguage(newLang);
  };

  // Scroll detection for header style
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setIsMegaMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  // Main navigation
  const mainNav: NavItem[] = [
    { key: "home", path: "/", icon: Sparkles, label: { ar: "الرئيسية", en: "Home" } },
    { key: "about", path: "/about", icon: Shield, label: { ar: "من نحن", en: "About" } },
    { key: "sports", path: "/sports", icon: Trophy, label: { ar: "الرياضات", en: "Sports" } },
    { key: "branches", path: "/branches", icon: Users, label: { ar: "الفروع", en: "Branches" } },
    { key: "news", path: "/news", icon: Newspaper, label: { ar: "الأخبار", en: "News" } },
    { key: "achievements", path: "/achievements", icon: Flame, label: { ar: "الإنجازات", en: "Achievements" } },
  ];

  // Mega menu categories
  const megaMenuSections = [
    {
      title: { ar: "المجتمع والتأثير", en: "Community & Impact" },
      items: [
        { key: "humanitarian", path: "/humanitarian", label: { ar: "الدور الإنساني", en: "Humanitarian" } },
        { key: "warImpact", path: "/war-impact", label: { ar: "تأثير الحرب", en: "War Impact" } },
        { key: "championsWithoutBorders", path: "/champions-without-borders", label: { ar: "أبطال بلا حدود", en: "Champions Without Borders" } },
      ],
    },
    {
      title: { ar: "قصصنا", en: "Our Stories" },
      items: [
        { key: "successStories", path: "/success-stories", label: { ar: "قصص النجاح", en: "Success Stories" } },
        { key: "testimonials", path: "/testimonials", label: { ar: "آراء العملاء", en: "Testimonials" } },
        { key: "press", path: "/press", label: { ar: "الصحافة", en: "Press" } },
      ],
    },
    {
      title: { ar: "انضم إلينا", en: "Join Us" },
      items: [
        { key: "team", path: "/team", label: { ar: "فريق العمل", en: "Team" } },
        { key: "partners", path: "/partners", label: { ar: "الشركاء", en: "Partners" } },
        { key: "activities", path: "/activities", label: { ar: "الأنشطة والرحلات", en: "Activities" } },
      ],
    },
  ];

  // Social links
  const socialLinks: SocialLink[] = [
    { key: "facebook", href: "#", label: { ar: "فيسبوك", en: "Facebook" }, Icon: Facebook, color: "#1877F2" },
    { key: "instagram", href: "#", label: { ar: "إنستغرام", en: "Instagram" }, Icon: Instagram, color: "#E4405F" },
    { key: "youtube", href: "#", label: { ar: "يوتيوب", en: "YouTube" }, Icon: Youtube, color: "#FF0000" },
    { key: "x", href: "#", label: { ar: "إكس", en: "X" }, Icon: Twitter, color: "#000000" },
    { key: "linkedin", href: "#", label: { ar: "لينكدإن", en: "LinkedIn" }, Icon: Linkedin, color: "#0A66C2" },
    { key: "tiktok", href: "#", label: { ar: "تيك توك", en: "TikTok" }, Icon: TikTokIcon, color: "#000000" },
    { key: "whatsapp", href: "#", label: { ar: "واتساب", en: "WhatsApp" }, Icon: MessageCircle, color: "#25D366" },
    { key: "email", href: "mailto:info@champions.ps", label: { ar: "البريد", en: "Email" }, Icon: Mail, color: "#EA4335" },
    { key: "phone", href: "#", label: { ar: "هاتف", en: "Phone" }, Icon: Phone, color: "#FFA92C" },
  ];

  const navLabel = (ar: string, en: string) => (isRTL ? ar : en);

  return (
    <>
      {/* Header */}
      <motion.header
        ref={headerRef}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 100, damping: 20 }}
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
          scrolled
            ? "glass border-b border-border/40 shadow-lg shadow-primary/5"
            : "bg-background/60 backdrop-blur-sm"
        )}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20 lg:h-24">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-3 group relative z-10"
            >
              <div className="relative">
                <motion.img
                  src={logo}
                  alt="Champions Academy"
                  className="h-16 lg:h-20 w-auto"
                  whileHover={{ scale: 1.05, rotate: 2 }}
                  transition={{ type: "spring", stiffness: 300 }}
                />
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-primary/30 to-champions-accent/30 rounded-full blur-2xl"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.5, 0.8, 0.5],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              </div>

              <div className="hidden sm:flex flex-col leading-tight">
                <motion.span
                  className="text-lg lg:text-xl font-bold gradient-text"
                  whileHover={{ scale: 1.02 }}
                >
                  {navLabel("أكاديمية تشامبيونز", "Champions Academy")}
                </motion.span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {mainNav.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.key}
                    to={item.path}
                    end={item.path === "/"}
                    className={({ isActive }) =>
                      cn(
                        "group relative px-4 py-2 rounded-xl transition-all duration-300",
                        isActive
                          ? "text-primary"
                          : "text-muted-foreground hover:text-foreground"
                      )
                    }
                  >
                    {({ isActive }) => (
                      <motion.div
                        className="flex items-center gap-2"
                        whileHover={{ y: -2 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="font-medium">{navLabel(item.label.ar, item.label.en)}</span>

                        {/* Active indicator */}
                        {isActive && (
                          <motion.div
                            layoutId="activeNav"
                            className="absolute inset-0 bg-gradient-to-r from-primary/10 to-champions-accent/10 rounded-xl -z-10"
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                          />
                        )}

                        {/* Hover glow */}
                        <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/5 to-primary/0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
                      </motion.div>
                    )}
                  </NavLink>
                );
              })}

              {/* More Button */}
              <motion.button
                onClick={() => setIsMegaMenuOpen(!isMegaMenuOpen)}
                className={cn(
                  "relative px-4 py-2 rounded-xl transition-all duration-300 flex items-center gap-2 font-medium",
                  isMegaMenuOpen
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-foreground"
                )}
                whileHover={{ y: -2 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Sparkles className="h-4 w-4" />
                {navLabel("المزيد", "More")}
                <motion.div
                  animate={{ rotate: isMegaMenuOpen ? 90 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ChevronRight className={cn("h-4 w-4", isRTL && "rotate-180")} />
                </motion.div>
              </motion.button>
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-2">
              {/* Language Toggle */}
              <motion.button
                onClick={toggleLanguage}
                className="relative h-10 w-10 rounded-full flex items-center justify-center group overflow-hidden"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label={navLabel("تبديل اللغة", "Toggle language")}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-champions-accent/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                <Globe className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-all duration-300 group-hover:rotate-12" />
              </motion.button>

              {/* Booking CTA */}
              <Link to="/booking" className="hidden sm:block">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button className="relative overflow-hidden group btn-glow px-6">
                    <span className="relative z-10 font-semibold flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      {navLabel("احجز الآن", "Book Now")}
                    </span>
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                      animate={{
                        x: ["-100%", "100%"],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    />
                  </Button>
                </motion.div>
              </Link>

              {/* Mobile Menu Toggle */}
              <motion.button
                onClick={() => setIsMenuOpen(true)}
                className="lg:hidden h-10 w-10 rounded-full flex items-center justify-center hover:bg-primary/10 transition-colors"
                whileTap={{ scale: 0.95 }}
                aria-label={navLabel("فتح القائمة", "Open menu")}
              >
                <Menu className="h-6 w-6" />
              </motion.button>
            </div>
          </div>
        </div>

        {/* Mega Menu */}
        <AnimatePresence>
          {isMegaMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="absolute top-full left-0 right-0 glass border-t border-border/40 shadow-2xl"
            >
              <div className="container mx-auto px-4 py-8">
                <div className="grid grid-cols-3 gap-8">
                  {megaMenuSections.map((section, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="space-y-4"
                    >
                      <h3 className="text-sm font-bold text-primary flex items-center gap-2">
                        <div className="h-1 w-8 bg-gradient-to-r from-primary to-champions-accent rounded-full" />
                        {navLabel(section.title.ar, section.title.en)}
                      </h3>

                      <div className="space-y-1">
                        {section.items.map((item) => (
                          <Link
                            key={item.key}
                            to={item.path}
                            className="group block px-4 py-3 rounded-lg hover:bg-surface-2 transition-all duration-300"
                            onClick={() => setIsMegaMenuOpen(false)}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                                {navLabel(item.label.ar, item.label.en)}
                              </span>
                              <ChevronRight className={cn(
                                "h-4 w-4 text-muted-foreground group-hover:text-primary transition-all opacity-0 group-hover:opacity-100",
                                isRTL ? "rotate-180 group-hover:-translate-x-1" : "group-hover:translate-x-1"
                              )} />
                            </div>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Quick Actions */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="mt-8 pt-8 border-t border-border/40 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground">{navLabel("تابعنا", "Follow us")}</span>
                    <div className="flex gap-2">
                      {socialLinks.map((social) => {
                        const Icon = social.Icon;
                        return (
                          <motion.a
                            key={social.key}
                            href={social.href}
                            target="_blank"
                            rel="noreferrer"
                            className="h-8 w-8 rounded-full flex items-center justify-center border border-border/40 hover:border-primary/40 transition-colors group"
                            whileHover={{ scale: 1.1, y: -2 }}
                            aria-label={navLabel(social.label.ar, social.label.en)}
                          >
                            <Icon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                          </motion.a>
                        );
                      })}
                    </div>
                  </div>

                  <Link to="/contact">
                    <Button variant="outline" size="sm" className="gap-2">
                      <Mail className="h-4 w-4" />
                      {navLabel("تواصل معنا", "Contact Us")}
                    </Button>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
              className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 lg:hidden"
            />

            {/* Drawer */}
            <motion.div
              initial={{ x: isRTL ? "100%" : "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: isRTL ? "100%" : "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className={cn(
                "fixed top-0 bottom-0 w-80 max-w-[85vw] glass border-border/40 z-50 lg:hidden overflow-y-auto",
                isRTL ? "right-0 border-l" : "left-0 border-r"
              )}
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <img src={logo} alt="Champions Academy" className="h-10 w-auto" />
                    <div className="flex flex-col leading-tight">
                      <span className="font-bold text-foreground">{navLabel("أكاديمية تشامبيونز", "Champions Academy")}</span>
                      <span className="text-xs text-muted-foreground">{navLabel("جزء من مجموعة تشامبيونز", "Part of Champions Group")}</span>
                    </div>
                  </div>

                  <motion.button
                    onClick={() => setIsMenuOpen(false)}
                    className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-surface-2 transition-colors"
                    whileTap={{ scale: 0.9 }}
                  >
                    <X className="h-6 w-6" />
                  </motion.button>
                </div>

                {/* Main Nav */}
                <nav className="space-y-2 mb-8">
                  {mainNav.map((item, idx) => {
                    const Icon = item.icon;
                    return (
                      <motion.div
                        key={item.key}
                        initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.05 }}
                      >
                        <NavLink
                          to={item.path}
                          end={item.path === "/"}
                          className={({ isActive }) =>
                            cn(
                              "flex items-center gap-3 px-4 py-3 rounded-xl transition-all",
                              isActive
                                ? "bg-gradient-to-r from-primary/10 to-champions-accent/10 text-primary border border-primary/30"
                                : "text-foreground hover:bg-surface-2"
                            )
                          }
                        >
                          <Icon className="h-5 w-5" />
                          <span className="font-medium">{navLabel(item.label.ar, item.label.en)}</span>
                        </NavLink>
                      </motion.div>
                    );
                  })}
                </nav>

                {/* Mega Menu Sections */}
                {megaMenuSections.map((section, sectionIdx) => (
                  <motion.div
                    key={sectionIdx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + sectionIdx * 0.1 }}
                    className="mb-6"
                  >
                    <h4 className="text-xs font-bold text-primary mb-3 px-4">{navLabel(section.title.ar, section.title.en)}</h4>
                    <div className="space-y-1">
                      {section.items.map((item) => (
                        <Link
                          key={item.key}
                          to={item.path}
                          className="block px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-surface-2 transition-all"
                        >
                          {navLabel(item.label.ar, item.label.en)}
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                ))}

                {/* Social Links */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="pt-6 border-t border-border/40"
                >
                  <h4 className="text-xs font-bold text-primary mb-3 px-4">{navLabel("تابعنا", "Follow us")}</h4>
                  <div className="grid grid-cols-4 gap-2">
                    {socialLinks.map((social) => {
                      const Icon = social.Icon;
                      return (
                        <motion.a
                          key={social.key}
                          href={social.href}
                          target="_blank"
                          rel="noreferrer"
                          className="flex flex-col items-center gap-1 p-3 rounded-lg border border-border/40 hover:border-primary/40 hover:bg-surface-2 transition-all group"
                          whileTap={{ scale: 0.95 }}
                        >
                          <Icon className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                          <span className="text-[10px] text-muted-foreground">{navLabel(social.label.ar, social.label.en)}</span>
                        </motion.a>
                      );
                    })}
                  </div>
                </motion.div>

                {/* CTA */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="mt-6 space-y-2"
                >
                  <Link to="/booking" className="block">
                    <Button className="w-full btn-glow">
                      <Sparkles className="h-4 w-4 mr-2" />
                      {navLabel("احجز الآن", "Book Now")}
                    </Button>
                  </Link>

                  <Link to="/contact" className="block">
                    <Button variant="outline" className="w-full">
                      <Mail className="h-4 w-4 mr-2" />
                      {navLabel("تواصل معنا", "Contact Us")}
                    </Button>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Click outside mega menu to close */}
      {isMegaMenuOpen && (
        <div
          className="fixed inset-0 z-40 hidden lg:block"
          onClick={() => setIsMegaMenuOpen(false)}
        />
      )}
    </>
  );
};

export default Header;
